/**
 * useRecording Hook
 *
 * Manages session recording state and operations:
 * - Start/stop recording
 * - Track recording status and duration
 * - Handle recording permissions
 */

import { useState, useCallback, useEffect } from 'react';
import { apiClient } from '@/services/api.client';
import type {
  SessionRecording,
  StartRecordingRequest,
  RecordingStartedEvent,
  RecordingStoppedEvent,
  RecordingStatusChangedEvent
} from '@/types/api.types';
import socketClient from '@/services/socket.client';
import { asApiError } from '@/utils/error.util';
// Toast functionality will be implemented later
const showToast = (options: { type: string; message: string; duration: number }) => {
  console.log(`[${options.type.toUpperCase()}] ${options.message}`);
};

export interface RecordingState {
  activeRecording: SessionRecording | null;
  isRecording: boolean;
  isStarting: boolean;
  isStopping: boolean;
  recordingDuration: number; // Seconds elapsed
  error: string | null;
}

export const useRecording = (sessionId?: string) => {
  const [state, setState] = useState<RecordingState>({
    activeRecording: null,
    isRecording: false,
    isStarting: false,
    isStopping: false,
    recordingDuration: 0,
    error: null,
  });

  /**
   * Timer for recording duration display
   */
  useEffect(() => {
    if (!state.isRecording || !state.activeRecording) {
      return;
    }

    const startTime = new Date(state.activeRecording.startedAt).getTime();
    const updateDuration = () => {
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000);
      setState(prev => ({ ...prev, recordingDuration: elapsed }));
    };

    // Update immediately
    updateDuration();

    // Then update every second
    const interval = setInterval(updateDuration, 1000);

    return () => clearInterval(interval);
  }, [state.isRecording, state.activeRecording]);

  /**
   * Start recording the current voice session
   */
  const startRecording = useCallback(async (options?: { title?: string; includeVideo?: boolean }) => {
    if (!sessionId) {
      setState(prev => ({ ...prev, error: 'No active voice session' }));
      return;
    }

    try {
      setState(prev => ({ ...prev, isStarting: true, error: null }));

      const request: StartRecordingRequest = {
        sessionId,
        title: options?.title,
        includeVideo: options?.includeVideo ?? false,
      };

      const response = await apiClient.post<{ success: boolean; recording: SessionRecording }>(
        '/recordings/start',
        request
      );

      if (response.data.success) {
        setState(prev => ({
          ...prev,
          activeRecording: response.data.recording,
          isRecording: true,
          isStarting: false,
          recordingDuration: 0,
        }));

        showToast({
          type: 'success',
          message: 'Записването започна',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Failed to start recording:', error);

      const errorMessage = asApiError(error).response?.data?.error || 'Неуспешно започване на запис';
      setState(prev => ({
        ...prev,
        isStarting: false,
        error: errorMessage,
      }));

      showToast({
        type: 'error',
        message: errorMessage,
        duration: 5000,
      });
    }
  }, [sessionId]);

  /**
   * Stop the current recording
   */
  const stopRecording = useCallback(async () => {
    if (!state.activeRecording) {
      return;
    }

    try {
      setState(prev => ({ ...prev, isStopping: true, error: null }));

      await apiClient.post(`/recordings/${state.activeRecording.id}/stop`);

      setState(prev => ({
        ...prev,
        isStopping: false,
      }));

      showToast({
        type: 'success',
        message: 'Записването спря',
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to stop recording:', error);

      const errorMessage = asApiError(error).response?.data?.error || 'Неуспешно спиране на запис';
      setState(prev => ({
        ...prev,
        isStopping: false,
        error: errorMessage,
      }));

      showToast({
        type: 'error',
        message: errorMessage,
        duration: 5000,
      });
    }
  }, [state.activeRecording]);

  /**
   * WebSocket event handlers
   */
  useEffect(() => {
    if (!sessionId) return;

    const socket = socketClient.getSocket();
    if (!socket) return;

    // Recording started by someone
    const handleRecordingStarted = (event: RecordingStartedEvent) => {
      if (event.sessionId !== sessionId) return;

      console.log('📹 Recording started:', event);

      setState(prev => ({
        ...prev,
        activeRecording: event.recording,
        isRecording: true,
        isStarting: false,
        recordingDuration: 0,
      }));

      showToast({
        type: 'info',
        message: `${event.startedByUser.displayName} започна записване`,
        duration: 4000,
      });
    };

    // Recording stopped by someone
    const handleRecordingStopped = (event: RecordingStoppedEvent) => {
      if (event.sessionId !== sessionId) return;

      console.log('⏹️ Recording stopped:', event);

      setState(prev => ({
        ...prev,
        activeRecording: null,
        isRecording: false,
        isStopping: false,
        recordingDuration: 0,
      }));

      showToast({
        type: 'info',
        message: `${event.stoppedByUser.displayName} спря записването`,
        duration: 4000,
      });
    };

    // Recording status changed (started processing, completed, failed)
    const handleRecordingStatusChanged = (event: RecordingStatusChangedEvent) => {
      if (event.sessionId !== sessionId) return;

      console.log('🔄 Recording status changed:', event);

      setState(prev => {
        if (!prev.activeRecording || prev.activeRecording.id !== event.recordingId) {
          return prev;
        }

        return {
          ...prev,
          activeRecording: {
            ...prev.activeRecording,
            recordingStatus: event.status,
          },
        };
      });

      // Show notification for completed/failed recordings
      if (event.status === 'completed') {
        showToast({
          type: 'success',
          message: 'Записът е готов',
          duration: 4000,
        });
      } else if (event.status === 'failed') {
        showToast({
          type: 'error',
          message: event.error || 'Записът се провали',
          duration: 5000,
        });
      }
    };

    // Register event listeners
    socket.on('recording:started', handleRecordingStarted);
    socket.on('recording:stopped', handleRecordingStopped);
    socket.on('recording:status-changed', handleRecordingStatusChanged);

    // Cleanup
    return () => {
      socket.off('recording:started', handleRecordingStarted);
      socket.off('recording:stopped', handleRecordingStopped);
      socket.off('recording:status-changed', handleRecordingStatusChanged);
    };
  }, [sessionId]);

  /**
   * Format duration for display (MM:SS)
   */
  const formatDuration = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  return {
    ...state,
    startRecording,
    stopRecording,
    formatDuration,
  };
};
